import { useState, useEffect } from 'react'
import notify from 'components/atoms/Notify'
import apiPost from 'lib/network/apiPost'
import { useHistory, useParams } from 'react-router-dom'
import { EyeIcon, EyeOffIcon } from '@heroicons/react/outline'

const PasswordRest = ({ fetchUser, close }) => {
  const [passwords, setPasswords] = useState({ password: '', confirmPassword: '' })
  const [targetRole, setTargetRole] = useState('admin')
  const [setPasswordByEmail, setSetPasswordByEmail] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [isSubmitting, setIsSubmitting] = useState(false)
  const history = useHistory()
  const { id } = useParams()

  const canSetPasswordByEmail = targetRole === 'admin' || targetRole === 'user'

  useEffect(() => {
    setPasswords({ password: '', confirmPassword: '' })
    setShowPassword(false)
    setShowConfirmPassword(false)
  }, [])

  const handleTargetRoleChange = value => {
    setTargetRole(value)
    if (value === 'company') {
      setSetPasswordByEmail(false)
    }
  }

  const handleSetPasswordByEmailChange = e => {
    const checked = e.target.checked
    setSetPasswordByEmail(checked)
    if (checked) {
      setPasswords({ password: '', confirmPassword: '' })
    }
  }

  const handlePasswordChange = async () => {
    if (isSubmitting) return

    if (!setPasswordByEmail) {
      if (!passwords?.password || !passwords?.confirmPassword) {
        notify.error('Passwords and confirmation password are required')
        return
      }
      if (passwords.password !== passwords.confirmPassword) {
        notify.error('Passwords do not match')
        return
      }
    }

    setIsSubmitting(true)
    try {
      const response = await apiPost(
        `user/reset-password/${id}`,
        setPasswordByEmail
          ? { role: targetRole, setPasswordByEmail: true }
          : {
              role: targetRole,
              password: passwords.password,
              confirmPassword: passwords.confirmPassword,
            },
        0,
        false
      )

      if (response.success) {
        notify.success(setPasswordByEmail ? 'Password setup email sent successfully' : 'Password Updated successfully')
        close()
        fetchUser()
        history.push(`/users/edit/${response.data._id}/user-profile`)
      } else {
        notify.error(response.message || 'Failed to update password')
      }
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div>
      <div className="mb-3">
        <label htmlFor="email" className="block text-sm font-normal text-gray-700 mb-1">
          Reset Password for:
        </label>
        <div className="mt-2">
          <div className="flex items-center justify-between space-x-4">
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="role"
                value="admin"
                checked={targetRole === 'admin'}
                onChange={e => handleTargetRoleChange(e.target.value)}
                className="focus:ring-dark-500 h-4 w-4 text-dark-600 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Dashboard</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="role"
                value="company"
                checked={targetRole === 'company'}
                onChange={e => handleTargetRoleChange(e.target.value)}
                className="focus:ring-dark-500 h-4 w-4 text-dark-600 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">Office App</span>
            </label>
            <label className="inline-flex items-center">
              <input
                type="radio"
                name="role"
                value="user"
                checked={targetRole === 'user'}
                onChange={e => handleTargetRoleChange(e.target.value)}
                className="focus:ring-dark-500 h-4 w-4 text-dark-600 border-gray-300"
              />
              <span className="ml-2 text-sm text-gray-700">User</span>
            </label>
          </div>
        </div>
      </div>
      <div className="mb-6">
        <label htmlFor="password" className="block text-sm font-normal text-gray-700 mb-1">
          Password
        </label>
        <div className="mt-1 sm:mt-0 sm:col-span-2 relative">
          <input
            type={showPassword ? 'text' : 'password'}
            name="password"
            id="password"
            value={passwords.password}
            disabled={setPasswordByEmail}
            onChange={e => setPasswords(prev => ({ ...prev, password: e.target.value }))}
            autoComplete="new-password"
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            data-bwignore="true"
            data-dashlane-ignore="true"
            className="block w-full pr-10 shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            disabled={setPasswordByEmail}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none disabled:cursor-not-allowed"
            onClick={() => setShowPassword(!showPassword)}
            aria-label={showPassword ? 'Hide password' : 'Show password'}
          >
            {showPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <div className="mb-6">
        <label htmlFor="confirmPassword" className="block text-sm font-normal text-gray-700 mb-1">
          Confirm Password
        </label>
        <div className="mt-1 sm:mt-0 sm:col-span-2 relative">
          <input
            type={showConfirmPassword ? 'text' : 'password'}
            name="confirmPassword"
            id="confirmPassword"
            value={passwords.confirmPassword}
            disabled={setPasswordByEmail}
            onChange={e => setPasswords(prev => ({ ...prev, confirmPassword: e.target.value }))}
            autoComplete="new-password"
            data-lpignore="true"
            data-form-type="other"
            data-1p-ignore="true"
            data-bwignore="true"
            data-dashlane-ignore="true"
            className="block w-full pr-10 shadow-sm focus:ring-dark-500 focus:border-dark-500 sm:text-sm border-gray-300 rounded-md disabled:bg-gray-100 disabled:cursor-not-allowed"
          />
          <button
            type="button"
            disabled={setPasswordByEmail}
            className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 focus:outline-none disabled:cursor-not-allowed"
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
          >
            {showConfirmPassword ? <EyeOffIcon className="h-5 w-5" /> : <EyeIcon className="h-5 w-5" />}
          </button>
        </div>
        {canSetPasswordByEmail && (
          <label className="mt-2 flex items-center gap-2 text-sm text-gray-700">
            <input type="checkbox" checked={setPasswordByEmail} onChange={handleSetPasswordByEmailChange} className="rounded border-gray-300 text-dark-600 focus:ring-dark-500" />
            Set password by email
          </label>
        )}
      </div>

      <div className="mt-4 flex justify-end">
        <button
          type="button"
          disabled={isSubmitting}
          className={`inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm text-normal rounded-md text-white bg-dark-600 hover:bg-dark-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-dark-500 sm:ml-3 ${
            isSubmitting ? 'opacity-50 cursor-not-allowed' : ''
          }`}
          onClick={handlePasswordChange}
        >
          {isSubmitting ? (
            <span className="flex items-center">
              <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path
                  className="opacity-75"
                  fill="currentColor"
                  d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                ></path>
              </svg>
              <span>Please wait...</span>
            </span>
          ) : setPasswordByEmail ? (
            'Send Setup Email'
          ) : (
            'Update Password'
          )}
        </button>

        <button
          type="button"
          disabled={isSubmitting}
          className="ml-2 inline-flex justify-center rounded-md border border-transparent bg-gray-100 px-4 py-2 text-sm font-medium text-gray-900 hover:bg-gray-200 focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed"
          onClick={() => close()}
        >
          Close
        </button>
      </div>
    </div>
  )
}

export default PasswordRest
